import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/system';
import { IconButton } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';

import { ReplayIcon } from '@/assets/icons';
import { PropertiesLabel } from '@modules/common/components/PropertiesLabel';
import { Accordion } from '@/modules/common/components/Accordion';
import { NumberInput } from '@/modules/common/components/inputs';
import { StyledTooltip } from '@modules/common/components/StyledTooltip';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

import { SimulationServiceFactory } from '../../service/SimulationServiceFactory';

const DEFAULT_VALUE = 100;

function UvtOverrides() {
  const { t } = useTranslation();
  const simSvc = useMemo(
    () => SimulationServiceFactory.getInstance().createSimulationService(),
    [],
  );
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const { config } = simSvc.useDraft();

  const handleMaxForwardSpeedChange = useCallback(
    (value: number) => {
      simSvc.updateDraft({
        config: {
          uvtOverrides: {
            maxForwardSpeedPerc: value,
          },
        },
      });
      fddmSvc.setPreference('simulationConfig.uvtOverrides.maxForwardSpeedPerc', value);
    },
    [simSvc, fddmSvc],
  );

  const handleResetMaxForwardSpeed = useCallback(() => {
    handleMaxForwardSpeedChange(DEFAULT_VALUE);
  }, [handleMaxForwardSpeedChange]);

  const handleMaxReverseSpeedChange = useCallback(
    (value: number) => {
      simSvc.updateDraft({
        config: {
          uvtOverrides: {
            maxReverseSpeedPerc: value,
          },
        },
      });
      fddmSvc.setPreference('simulationConfig.uvtOverrides.maxReverseSpeedPerc', value);
    },
    [simSvc, fddmSvc],
  );

  const handleResetMaxReverseSpeed = useCallback(() => {
    handleMaxReverseSpeedChange(DEFAULT_VALUE);
  }, [handleMaxReverseSpeedChange]);

  const handleLoadHandlingDurationChange = useCallback(
    (value: number) => {
      simSvc.updateDraft({
        config: {
          uvtOverrides: {
            loadHandlingDurationPerc: value,
          },
        },
      });
      fddmSvc.setPreference('simulationConfig.uvtOverrides.loadHandlingDurationPerc', value);
    },
    [fddmSvc, simSvc],
  );

  const handleResetLoadHandlingDuration = useCallback(() => {
    handleLoadHandlingDurationChange(DEFAULT_VALUE);
  }, [handleLoadHandlingDurationChange]);

  return (
    <Accordion titleKey='interface:simulation.experimental_settings.uvt_overrides.title'>
      <Stack spacing={2}>
        <Stack direction='row' spacing={1}>
          <PropertiesLabel
            labelRatio={65}
            i18nkey='interface:simulation.experimental_settings.uvt_overrides.fields.max_forward_speed'
          >
            <NumberInput
              value={config.uvtOverrides?.maxForwardSpeedPerc ?? DEFAULT_VALUE}
              decimals={0}
              unit='%'
              minValue={1}
              minValueErrorMsg={t('common:minimum_value', { minValue: 1 })}
              onChange={handleMaxForwardSpeedChange}
            />
          </PropertiesLabel>

          <StyledTooltip
            title='Revert'
            placement='bottom'
            disabled={config.uvtOverrides?.maxForwardSpeedPerc === DEFAULT_VALUE}
          >
            <IconButton
              onClick={handleResetMaxForwardSpeed}
              disabled={config.uvtOverrides?.maxForwardSpeedPerc === DEFAULT_VALUE}
            >
              <ReplayIcon />
            </IconButton>
          </StyledTooltip>
        </Stack>
        <Stack direction='row' spacing={1}>
          <PropertiesLabel
            labelRatio={65}
            i18nkey='interface:simulation.experimental_settings.uvt_overrides.fields.max_reverse_speed'
          >
            <NumberInput
              value={config.uvtOverrides?.maxReverseSpeedPerc ?? DEFAULT_VALUE}
              decimals={0}
              unit='%'
              minValue={1}
              minValueErrorMsg={t('common:minimum_value', { minValue: 1 })}
              onChange={handleMaxReverseSpeedChange}
            />
          </PropertiesLabel>
          <StyledTooltip
            title='Revert'
            placement='bottom'
            disabled={config.uvtOverrides?.maxReverseSpeedPerc === DEFAULT_VALUE}
          >
            <IconButton
              onClick={handleResetMaxReverseSpeed}
              disabled={config.uvtOverrides?.maxReverseSpeedPerc === DEFAULT_VALUE}
            >
              <ReplayIcon />
            </IconButton>
          </StyledTooltip>
        </Stack>
        <Stack direction='row' spacing={1}>
          <PropertiesLabel
            labelRatio={65}
            infoI18nKey='interface:simulation.experimental_settings.uvt_overrides.info'
            i18nkey='interface:simulation.experimental_settings.uvt_overrides.fields.load_transfer_speed'
          >
            <NumberInput
              value={config.uvtOverrides?.loadHandlingDurationPerc ?? DEFAULT_VALUE}
              decimals={0}
              unit='%'
              minValue={1}
              minValueErrorMsg={t('common:minimum_value', { minValue: 1 })}
              onChange={handleLoadHandlingDurationChange}
            />
          </PropertiesLabel>
          <StyledTooltip
            title='Revert'
            placement='bottom'
            disabled={config.uvtOverrides?.loadHandlingDurationPerc === DEFAULT_VALUE}
          >
            <IconButton
              onClick={handleResetLoadHandlingDuration}
              disabled={config.uvtOverrides?.loadHandlingDurationPerc === DEFAULT_VALUE}
            >
              <ReplayIcon />
            </IconButton>
          </StyledTooltip>
        </Stack>
      </Stack>
    </Accordion>
  );
}

export default memo(UvtOverrides);
